import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle, XCircle, Search, DollarSign, Calendar, User, Eye, Image } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProofImage = ({ url }) => {
  const [failed, setFailed] = React.useState(false);
  if (failed) {
    return (
      <p className="text-gray-500 p-8">
        No se pudo cargar la imagen.{' '}
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Abrir enlace</a>
      </p>
    );
  }
  return (
    <img 
      src={url} 
      alt="Comprobante de pago" 
      className="max-w-full max-h-[75vh] object-contain rounded"
      onError={() => setFailed(true)}
    />
  );
};

const PaymentManagement = ({ token, clients }) => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    status: 'todos',
    clientId: 'todos',
    paymentMethod: 'todos',
    dateFrom: '',
    dateTo: ''
  });

  const [proofModalUrl, setProofModalUrl] = useState(null);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [payments, filters]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/payments`, axiosConfig);
      setPayments(response.data);
    } catch (error) {
      alert('Error al cargar los pagos');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...payments];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(p =>
        p.reference_number?.toLowerCase().includes(searchLower) ||
        p.payer_name?.toLowerCase().includes(searchLower) ||
        getClientName(p.client_id).toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (filters.status !== 'todos') {
      filtered = filtered.filter(p => p.status === filters.status);
    }

    // Client filter
    if (filters.clientId !== 'todos') {
      filtered = filtered.filter(p => p.client_id === filters.clientId);
    }

    // Payment method filter
    if (filters.paymentMethod !== 'todos') {
      filtered = filtered.filter(p => p.payment_method === filters.paymentMethod);
    }

    // Date range filter
    if (filters.dateFrom) {
      filtered = filtered.filter(p => new Date(p.payment_date) >= new Date(filters.dateFrom));
    }
    if (filters.dateTo) {
      filtered = filtered.filter(p => new Date(p.payment_date) <= new Date(filters.dateTo));
    }

    setFilteredPayments(filtered);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.full_name : 'Cliente Desconocido';
  };

  const handleVerifyPayment = async (paymentId) => {
    if (!window.confirm('¿Verificar este pago como válido?')) return;

    try {
      await axios.put(
        `${API}/admin/payments/${paymentId}/verify`,
        {},
        axiosConfig
      );
      alert('✅ Pago verificado exitosamente');
      fetchPayments();
    } catch (error) {
      alert('Error al verificar el pago: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRejectPayment = async (paymentId) => {
    const reason = window.prompt('Razón del rechazo:');
    if (!reason) return;

    try {
      await axios.put(
        `${API}/admin/payments/${paymentId}/reject`,
        { reason },
        axiosConfig
      );
      alert('✅ Pago rechazado');
      fetchPayments();
    } catch (error) {
      alert('Error al rechazar el pago: ' + error.message);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'verified':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'pending':
        return 'Pendiente';
      case 'verified':
        return 'Verificado';
      case 'rejected':
        return 'Rechazado';
      default:
        return status;
    }
  };

  const getTotalAmount = () => {
    return filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2);
  };

  const getTotalVerified = () => {
    return filteredPayments
      .filter(p => p.status === 'verified')
      .reduce((sum, p) => sum + (p.amount || 0), 0)
      .toFixed(2);
  };

  const getTotalPending = () => {
    return filteredPayments
      .filter(p => p.status === 'pending')
      .reduce((sum, p) => sum + (p.amount || 0), 0)
      .toFixed(2);
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Pagos</p>
                <p className="text-2xl font-bold">{filteredPayments.length}</p>
              </div>
              <DollarSign className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Monto Total</p>
                <p className="text-2xl font-bold text-blue-600">${getTotalAmount()}</p>
              </div>
              <DollarSign className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Verificados</p>
                <p className="text-2xl font-bold text-green-600">${getTotalVerified()}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pendientes</p>
                <p className="text-2xl font-bold text-yellow-600">${getTotalPending()}</p>
              </div>
              <Calendar className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Referencia, cliente..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label>Estado</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                  <SelectItem value="verified">Verificados</SelectItem>
                  <SelectItem value="rejected">Rechazados</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Cliente</Label>
              <Select value={filters.clientId} onValueChange={(v) => setFilters({...filters, clientId: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Método de Pago</Label>
              <Select value={filters.paymentMethod} onValueChange={(v) => setFilters({...filters, paymentMethod: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="Transferencia">Transferencia</SelectItem>
                  <SelectItem value="Zelle">Zelle</SelectItem>
                  <SelectItem value="PayPal">PayPal</SelectItem>
                  <SelectItem value="Efectivo">Efectivo</SelectItem>
                  <SelectItem value="Otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Fecha Desde</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
              />
            </div>

            <div>
              <Label>Fecha Hasta</Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payments List */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Pagos ({filteredPayments.length})</CardTitle>
          <CardDescription>Gestiona y verifica los pagos notificados por los clientes</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8">Cargando pagos...</p>
          ) : (
            <div className="space-y-3">
              {filteredPayments.map((payment) => (
                <div key={payment.id} className={`border-2 rounded-lg p-4 ${
                  payment.status === 'pending' ? 'border-yellow-300 bg-yellow-50' :
                  payment.status === 'verified' ? 'border-green-300 bg-green-50' :
                  'border-red-300 bg-red-50'
                }`}>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <User className="h-5 w-5 text-gray-600" />
                        <span className="font-bold text-lg">{getClientName(payment.client_id)}</span>
                        <span className={`px-4 py-1 rounded-full text-sm font-bold ${
                          payment.status === 'pending' ? 'bg-yellow-200 text-yellow-900' :
                          payment.status === 'verified' ? 'bg-green-200 text-green-900' :
                          'bg-red-200 text-red-900'
                        }`}>
                          {payment.status === 'pending' ? '⏳ EN REVISIÓN' :
                           payment.status === 'verified' ? '✅ APROBADO' : '❌ RECHAZADO'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600 text-xs">Monto</p>
                          <p className="font-bold text-green-600 text-xl">${payment.amount.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 text-xs">Método</p>
                          <p className="font-semibold">{payment.payment_method}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 text-xs">Referencia</p>
                          <p className="font-semibold font-mono text-xs">{payment.reference_number}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 text-xs">Fecha de Pago</p>
                          <p className="font-semibold">{new Date(payment.payment_date).toLocaleDateString()}</p>
                        </div>
                      </div>
                      {payment.notes && (
                        <div className="mt-3 bg-white p-3 rounded border text-sm">
                          <strong>Notas:</strong> {payment.notes}
                        </div>
                      )}
                      {(payment.receipt_file_url || payment.receipt_url) && (
                        <div className="mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const url = payment.receipt_file_url || payment.receipt_url;
                              // Prefix with BACKEND_URL if it's a relative path
                              const fullUrl = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
                              setProofModalUrl(fullUrl);
                            }}
                            className="gap-2 text-blue-700 border-blue-300 hover:bg-blue-50"
                            data-testid={`view-proof-${payment.id}`}
                          >
                            <Eye size={16} />
                            Ver Comprobante de Pago
                          </Button>
                        </div>
                      )}
                      {payment.status === 'verified' && payment.verified_at && (
                        <div className="mt-2 text-xs text-green-700 font-semibold">
                          ✓ Aprobado el {new Date(payment.verified_at).toLocaleString()}
                        </div>
                      )}
                      {payment.status === 'rejected' && payment.verified_at && (
                        <div className="mt-2 text-xs text-red-700 font-semibold">
                          ✗ Rechazado el {new Date(payment.verified_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 ml-4">
                      {payment.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleVerifyPayment(payment.id)}
                            className="bg-green-600 hover:bg-green-700 font-semibold"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Aprobar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRejectPayment(payment.id)}
                            className="text-red-600 border-red-600 hover:bg-red-50 font-semibold"
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Rechazar
                          </Button>
                        </>
                      )}
                      {payment.status === 'verified' && (
                        <div className="text-green-700 text-sm font-bold flex flex-col items-center bg-green-100 p-3 rounded">
                          <CheckCircle className="h-8 w-8 mb-1" />
                          APROBADO
                        </div>
                      )}
                      {payment.status === 'rejected' && (
                        <div className="text-red-700 text-sm font-bold flex flex-col items-center bg-red-100 p-3 rounded">
                          <XCircle className="h-8 w-8 mb-1" />
                          RECHAZADO
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {filteredPayments.length === 0 && (
                <div className="text-center py-12">
                  <DollarSign className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-500">No se encontraron pagos</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      {/* Proof Image Modal */}
      {proofModalUrl && (
        <div 
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setProofModalUrl(null)}
          data-testid="proof-modal-overlay"
        >
          <div className="relative max-w-3xl max-h-[90vh] bg-white rounded-xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b bg-gray-50">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <Image size={18} className="text-blue-600" />
                Comprobante de Pago
              </h3>
              <div className="flex items-center gap-2">
                <a 
                  href={proofModalUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  Abrir en nueva pestaña
                </a>
                <button 
                  onClick={() => setProofModalUrl(null)} 
                  className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                  data-testid="close-proof-modal"
                >
                  <XCircle size={22} className="text-gray-500" />
                </button>
              </div>
            </div>
            <div className="overflow-auto max-h-[80vh] flex items-center justify-center bg-gray-100 p-4">
              <ProofImage url={proofModalUrl} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentManagement;
